import { Card, Flex, SegmentedControl, Skeleton } from '@radix-ui/themes';
import { CanvasHelper } from 'classes/helpers/canvas.helper';
import { PlateCanvas } from 'classes/plate-canvas';
import { ErrorBoundary } from 'components/common/error-boundary';
import { PlotHeader } from 'components/sections/admin-portal/machines/dialogs/pitch-performance/plots/plot-header';
import { isAppearanceDark } from 'index';
import { TrajHelper } from 'lib_ts/classes/trajectory.helper';
import { MAX_PLATE_DELTA_IN } from 'lib_ts/enums/pitch-performance';
import { IGroupedFires } from 'lib_ts/interfaces/i-pitch-performance';
import { IPlateLoc } from 'lib_ts/interfaces/pitches';
import { useEffect, useRef, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import usePitchPerformanceStore from '../store/use-pitch-performance-store';

const COMPONENT_NAME = 'PitchPerfPlotPlate';

const REDRAW_DEQUEUE_MS = 100;

enum AimMode {
  Centered = 'Centered',
  Scattered = 'Scattered',
}

export const PitchPerfPlotPlate = (props: { groups: IGroupedFires[] }) => {
  const { loading, selectedShotIds, isAveraged, showThresholds, showTargets } =
    usePitchPerformanceStore(
      useShallow((state) => ({
        loading: state.loading,
        selectedShotIds: state.selectedShotIds,

        isAveraged: state.isAveraged,
        showThresholds: state.showThresholds,
        showTargets: state.showTargets,
      }))
    );

  const [aim, setAim] = useState(AimMode.Scattered);

  const [canvasDef] = useState(PlateCanvas.makeSquare(isAppearanceDark()));

  // what is used for assembly of the results
  const mergeCanvas = useRef<HTMLCanvasElement>(null);
  const mainCanvas = useRef<HTMLCanvasElement>(null);
  const shotsCanvas = useRef<HTMLCanvasElement>(null);

  // what gets seen by users + LogRocket
  const [imgSrc, setImgSrc] = useState<string>();
  // for dequeuing updates to the imgSrc
  const [lastUpdate, setLastUpdate] = useState(Date.now());
  const updateTimeout = useRef<NodeJS.Timeout>();

  // aggregate updates from various canvases before updating imgSrc
  useEffect(() => {
    updateTimeout.current = setTimeout(async () => {
      console.debug(`${COMPONENT_NAME}: update imgSrc`);

      const canvas = mergeCanvas.current;

      if (!canvas) {
        return;
      }

      // all the canvases that need to be combined should go here
      const sources = [mainCanvas.current, shotsCanvas.current].filter(
        (c) => c
      ) as HTMLCanvasElement[];

      const src = await CanvasHelper.combineToPNG({
        sources: sources,
        target: canvas,
      });

      setImgSrc(src);
    }, REDRAW_DEQUEUE_MS);

    return () => clearTimeout(updateTimeout.current);
  }, [lastUpdate]);

  // drawMain
  useEffect(() => {
    try {
      const canvas = mainCanvas.current;
      if (!canvas) {
        return;
      }

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        return;
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // canvasDef.drawStrikeZoneAutoAdjustArea(ctx);
      canvasDef.drawRulers(ctx, 12);
      canvasDef.drawStrikeZone(ctx, true);
      // canvasDef.drawGround(ctx);
    } catch (e) {
      console.error(e);
    } finally {
      setLastUpdate(Date.now());
    }
  }, [loading]);

  // drawShots
  useEffect(() => {
    const callback = async () => {
      try {
        const canvas = shotsCanvas.current;
        if (!canvas) {
          return;
        }

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          return;
        }

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        const centerPlate: IPlateLoc = {
          plate_x: canvasDef.CONFIG.x.default_ft,
          plate_z: canvasDef.CONFIG.y.default_ft,
        };

        if (aim === AimMode.Centered) {
          // draw a single (white) target dot in the middle
          if (showThresholds) {
            canvasDef.drawThreshold(ctx, {
              target: centerPlate,
              radiusIn: MAX_PLATE_DELTA_IN,
              fillColor: `rgba(255,255,255,25%)`,
              borderColor: `rgba(255,255,255,75%)`,
            });
          }

          if (showTargets) {
            await canvasDef.drawCrosshairs(ctx, {
              target: centerPlate,
              color: `rgba(150,150,150,100%)`,
              width_px: 20,
            });
          }
        }

        props.groups.forEach((p) => {
          const targetPlate =
            aim === AimMode.Centered ? centerPlate : p.target.plate;

          if (aim === AimMode.Scattered) {
            if (showThresholds) {
              canvasDef.drawThreshold(ctx, {
                target: targetPlate,
                radiusIn: MAX_PLATE_DELTA_IN,
                fillColor: `rgba(${p.rgb},25%)`,
                borderColor: `rgba(${p.rgb},75%)`,
              });
            }

            if (showTargets) {
              canvasDef.drawCrosshairs(ctx, {
                target: targetPlate,
                color: `rgba(${p.rgb},100%)`,
                width_px: 20,
              });
            }
          }

          // only used when aiming for center
          const transDelta: IPlateLoc =
            aim === AimMode.Centered
              ? // find the difference specifically between the actual target (not targetPlate) and the center
                {
                  plate_x: p.target.plate.plate_x - centerPlate.plate_x,
                  plate_z: p.target.plate.plate_z - centerPlate.plate_z,
                }
              : // translate by nothing if not aiming to center
                {
                  plate_x: 0,
                  plate_z: 0,
                };

          const avgPlate: IPlateLoc = {
            plate_x: p.average.plate.plate_x - transDelta.plate_x,
            plate_z: p.average.plate.plate_z - transDelta.plate_z,
          };

          if (isAveraged && showTargets) {
            canvasDef.drawLine(ctx, {
              from: avgPlate,
              to: targetPlate,
              lineColor: `rgba(${p.rgb},75%)`,
            });

            canvasDef.drawLabel(ctx, {
              value: avgPlate,
              target: targetPlate,
              fontColor: `rgba(${p.rgb},100%)`,
            });
          }

          if (isAveraged) {
            canvasDef.drawActual(ctx, {
              value: avgPlate,
              size: 3,
              fillColor: `rgba(${p.rgb},75%)`,
              borderColor: `rgba(${p.rgb},100%)`,
            });
            return;
          }

          // if the user has selected specific shots, only draw those
          const shots =
            selectedShotIds.length > 0
              ? p.rotatedShots.filter(({ _id }) =>
                  selectedShotIds.includes(_id)
                )
              : p.rotatedShots;

          shots.forEach((shot) => {
            if (!shot.traj) {
              return;
            }

            const transPlate = TrajHelper.getPlateLoc(
              TrajHelper.translate(shot.traj, {
                px: shot.traj.px - transDelta.plate_x,
                pz: shot.traj.pz - transDelta.plate_z,
                py: shot.traj.py,
              })
            );

            if (isAveraged && showTargets) {
              canvasDef.drawLine(ctx, {
                from: transPlate,
                to: targetPlate,
                lineColor: `rgba(${p.rgb},75%)`,
              });

              canvasDef.drawLabel(ctx, {
                value: transPlate,
                target: targetPlate,
                fontColor: `rgba(${p.rgb},100%)`,
              });
            }

            canvasDef.drawActual(ctx, {
              value: transPlate,
              size: 3,
              fillColor: `rgba(${p.rgb},75%)`,
              borderColor: `rgba(${p.rgb},100%)`,
            });
          });
        });
      } catch (e) {
        console.error(e);
      } finally {
        setLastUpdate(Date.now());
      }
    };

    callback();
  }, [
    props.groups,
    aim,
    isAveraged,
    showTargets,
    showThresholds,
    selectedShotIds,
  ]);

  return (
    <ErrorBoundary componentName="PitchPerfPlotPlate">
      <Skeleton loading={loading}>
        <Flex direction="column" gap="3">
          <PlotHeader
            title="Plate Location"
            subtitle="Plot of plate locations from catcher's view"
            children={
              <SegmentedControl.Root
                size="1"
                value={aim}
                onValueChange={(v) => setAim(v as AimMode)}
              >
                {Object.values(AimMode).map((o, i) => (
                  <SegmentedControl.Item key={`mode-${i}`} value={o}>
                    {o}
                  </SegmentedControl.Item>
                ))}
              </SegmentedControl.Root>
            }
          />

          <Card
            style={{
              padding: 0,
            }}
          >
            <div className="slider">
              <div
                className="sizer"
                style={{
                  aspectRatio: '1',
                }}
              >
                {/* for log rocket recordings to show the results */}
                {imgSrc && (
                  <img
                    alt="plate performance plot"
                    className="image-wrapper"
                    src={imgSrc}
                  />
                )}
                <canvas
                  ref={mergeCanvas}
                  data-identifier="merge-canvas"
                  width={canvasDef.CONFIG.canvas.width_px}
                  height={canvasDef.CONFIG.canvas.height_px}
                />
                <canvas
                  ref={mainCanvas}
                  data-identifier="main-canvas"
                  width={canvasDef.CONFIG.canvas.width_px}
                  height={canvasDef.CONFIG.canvas.height_px}
                />
                <canvas
                  ref={shotsCanvas}
                  data-identifier="shots-canvas"
                  width={canvasDef.CONFIG.canvas.width_px}
                  height={canvasDef.CONFIG.canvas.height_px}
                />
              </div>
            </div>
          </Card>
        </Flex>
      </Skeleton>
    </ErrorBoundary>
  );
};
